import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { MessageCircle, X, Send, Sparkles, Mic, Volume2, VolumeX } from "lucide-react";

const SID_KEY = "babimix_chat_sid";
const WHATSAPP = "https://wa.me/2250799483886?text=" + encodeURIComponent("Bonjour BABIMIX, j'ai une question.");
const SUGGESTIONS = ["Où sortir à Cocody ce soir ?", "Les meilleurs maquis d'Abidjan", "Comment réserver un billet ?"];

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [listening, setListening] = useState(false);
  const [speak, setSpeak] = useState(false);
  const sid = useRef(localStorage.getItem(SID_KEY) || "");
  const boxRef = useRef(null);
  const recogRef = useRef(null);
  const nav = useNavigate();
  const speechSupported = typeof window !== "undefined" && !!(window.SpeechRecognition || window.webkitSpeechRecognition);
  const ttsSupported = typeof window !== "undefined" && "speechSynthesis" in window;

  const speakText = (t) => {
    if (!ttsSupported) return;
    const clean = t.replace(/\[([^\]]+)\]\([^)]+\)/g, "$1").replace(/[*_#>`]/g, "");
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(clean);
    u.lang = "fr-FR";
    window.speechSynthesis.speak(u);
  };

  const toggleMic = () => {
    if (!speechSupported) return;
    if (listening) { if (recogRef.current) recogRef.current.stop(); return; }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new SR();
    rec.lang = "fr-FR"; rec.interimResults = true; rec.continuous = false;
    rec.onresult = (e) => setText(Array.from(e.results).map((r) => r[0].transcript).join(""));
    rec.onend = () => setListening(false);
    rec.onerror = () => setListening(false);
    recogRef.current = rec;
    setListening(true);
    rec.start();
  };

  useEffect(() => { if (boxRef.current) boxRef.current.scrollTop = boxRef.current.scrollHeight; }, [msgs, busy]);

  const renderRich = (text) => {
    const re = /\[([^\]]+)\]\(([^)]+)\)/g;
    const out = []; let last = 0, m, i = 0;
    while ((m = re.exec(text)) !== null) {
      if (m.index > last) out.push(text.slice(last, m.index));
      const href = m[2];
      out.push(
        <a key={i++} href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noreferrer"
          onClick={(e) => { if (href.startsWith("/")) { e.preventDefault(); setOpen(false); nav(href); } }}
          className="underline font-semibold text-[#FF5A00]">{m[1]}</a>
      );
      last = m.index + m[0].length;
    }
    if (last < text.length) out.push(text.slice(last));
    return out;
  };

  const ask = async (q) => {
    const question = (q || text).trim();
    if (!question || busy) return;
    setMsgs((m) => [...m, { role: "user", text: question }]);
    setText(""); setBusy(true);
    try {
      const { data } = await api.post("/chat", { message: question, session_id: sid.current || undefined });
      if (data.session_id) { sid.current = data.session_id; localStorage.setItem(SID_KEY, data.session_id); }
      setMsgs((m) => [...m, { role: "assistant", text: data.reply }]);
      if (speak) speakText(data.reply);
    } catch {
      setMsgs((m) => [...m, { role: "assistant", text: "Désolé, je suis momentanément indisponible. Réessayez dans un instant." }]);
    } finally { setBusy(false); }
  };

  return (
    <>
      <button data-testid="chat-widget-toggle" onClick={() => setOpen((o) => !o)}
        className="fixed bottom-20 lg:bottom-5 right-5 z-[60] w-14 h-14 rounded-full flame-gradient grid place-items-center shadow-lg shadow-black/40 hover:scale-105 transition-transform"
        aria-label="Assistant BABIMIX">
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>
      {open && (
        <div data-testid="chat-widget-panel"
          className="fixed bottom-36 lg:bottom-24 right-5 z-[60] w-[92vw] max-w-sm h-[70vh] max-h-[560px] rounded-2xl overflow-hidden flex flex-col card-surface shadow-2xl shadow-black/50 border border-white/10">
          <div className="px-4 py-3 flame-gradient flex items-center gap-2 font-bold text-white">
            <Sparkles className="w-5 h-5" /> <span className="flex-1">Assistant BABIMIX</span>
            {ttsSupported && (
              <button type="button" data-testid="chat-tts-toggle" title="Lecture à voix haute"
                onClick={() => { const n = !speak; setSpeak(n); if (!n) window.speechSynthesis.cancel(); }}
                className="opacity-90 hover:opacity-100">{speak ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}</button>
            )}
          </div>
          <div ref={boxRef} className="flex-1 overflow-y-auto p-4 space-y-3" style={{ background: "#FFF9F2" }}>
            {msgs.length === 0 && (
              <div className="text-sm text-slate-300 space-y-3">
                <p>Salut 👋 Je suis l'assistant BABIMIX. Demandez-moi par exemple :</p>
                <div className="flex flex-wrap gap-2">
                  {SUGGESTIONS.map((s) => (
                    <button key={s} data-testid="chat-suggestion" onClick={() => ask(s)}
                      className="text-xs bg-white/5 border border-white/10 rounded-full px-3 py-1.5 hover:bg-white/10 transition-colors">{s}</button>
                  ))}
                </div>
              </div>
            )}
            {msgs.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[82%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap ${m.role === "user" ? "flame-gradient text-white" : "bg-white/5 border border-white/10 text-slate-100"}`}>{m.role === "assistant" ? renderRich(m.text) : m.text}</div>
              </div>
            ))}
            {busy && <div className="flex justify-start"><div className="bg-white/5 border border-white/10 rounded-2xl px-4 py-2 text-sm text-slate-400 animate-pulse">L'assistant écrit…</div></div>}
          </div>
          <a href={WHATSAPP} target="_blank" rel="noreferrer" data-testid="chat-whatsapp"
            className="flex items-center justify-center gap-1.5 py-2 text-xs font-semibold text-[#25D366] hover:underline border-t border-white/10">
            <MessageCircle className="w-3.5 h-3.5" /> Parler à un humain sur WhatsApp
          </a>
          <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="p-3 border-t border-white/10 flex gap-2" style={{ background: "#FFFFFF" }}>
            <input data-testid="chat-widget-input" value={text} onChange={(e) => setText(e.target.value)} maxLength={1000}
              placeholder={listening ? "Parlez…" : "Posez votre question…"} className="flex-1 bg-white/5 border border-white/10 rounded-full px-4 py-2 text-sm outline-none focus:border-[#FF5A00]/60" />
            {speechSupported && (
              <button type="button" data-testid="chat-mic" onClick={toggleMic} title="Poser la question à l'oral"
                className={`w-10 h-10 grid place-items-center rounded-full shrink-0 border ${listening ? "bg-[#FF5A00] border-transparent text-white animate-pulse" : "border-white/15 bg-white/5 text-slate-300 hover:bg-white/10"}`}><Mic className="w-4 h-4" /></button>
            )}
            <button data-testid="chat-widget-send" type="submit" disabled={!text.trim() || busy}
              className="w-10 h-10 grid place-items-center rounded-full flame-gradient disabled:opacity-50 shrink-0"><Send className="w-4 h-4" /></button>
          </form>
        </div>
      )}
    </>
  );
}
